import ambientUrl from '../audio/ambient.mp3'
import { emit, getState, setState, storage, subscribe } from './app/store'

/**
 * Footer "play | mute" and the ambient sea sound.
 *
 * "play" turns the sound on everywhere and fires danino:play, which the snake
 * game on the home page answers by starting a round. "mute" turns all sound
 * off, game effects included (they read getState().soundOn). The audio element
 * lives outside the Swup container, so it keeps playing across page visits.
 *
 * Browsers block autoplay, so a remembered "on" only resumes with the first
 * interaction on the next visit that counts as a user gesture — a click, tap
 * or key press. If the browser still refuses, the preference is kept and the
 * next interaction tries again.
 */
const STORAGE_KEY = 'danino:sound'
const VOLUME = 0.45
const FADE_MS = 600

// Events that come with user activation (pointerdown does not for touch, so
// phones would refuse play()). pointerup counts for touch and pen only; a
// mouse is handled by its click.
const RESUME_EVENTS = ['pointerup', 'touchend', 'click', 'keydown']
// Keys that don't activate the page (Escape) or only prepare a shortcut.
const INERT_KEYS = new Set(['Escape', 'Shift', 'Control', 'Alt', 'Meta', 'CapsLock'])
const SOUND_BUTTONS = '[data-sound-play], [data-sound-mute]'

let audio = null
let fadeFrame = 0
// Bumped on every play / mute: a play() that settles after a newer choice
// belongs to the old one and must not act on it any more.
let request = 0
let resumeArmed = false

function ensureAudio() {
  if (!audio) {
    audio = new Audio(ambientUrl)
    audio.loop = true
    audio.preload = 'none'
    audio.volume = 0
  }
  return audio
}

function fadeTo(target, done) {
  cancelAnimationFrame(fadeFrame)
  const from = audio.volume
  const start = performance.now()
  const step = (now) => {
    // rAF timestamps can predate performance.now() taken above — clamp at 0.
    const progress = Math.min(1, Math.max(0, (now - start) / FADE_MS))
    audio.volume = from + (target - from) * progress
    if (progress < 1) {
      fadeFrame = requestAnimationFrame(step)
    } else {
      done?.()
    }
  }
  fadeFrame = requestAnimationFrame(step)
}

function syncButtons(soundOn) {
  document.querySelectorAll('[data-sound-play]').forEach((button) => {
    button.setAttribute('aria-pressed', String(soundOn))
  })
  document.querySelectorAll('[data-sound-mute]').forEach((button) => {
    button.setAttribute('aria-pressed', String(!soundOn))
  })
}

function play() {
  const id = request
  // With preload "none" play() only settles once the file has loaded.
  ensureAudio()
    .play()
    .then(
      () => {
        if (id === request) fadeTo(VOLUME)
      },
      (error) => {
        // Superseded, or interrupted by pause() (mute, background tab): the
        // newer call decides what happens.
        if (id !== request || error?.name === 'AbortError') return
        setState({ soundOn: false })
        // Refused for lack of a user gesture: keep the remembered "on" and
        // try again with the next interaction.
        if (error?.name === 'NotAllowedError' && storage.get(STORAGE_KEY) === 'on') armResume()
      }
    )
}

function apply(soundOn) {
  request += 1
  // A fade still running belongs to the previous choice — only the fade
  // started below may finish (and a fade-out always ends paused).
  cancelAnimationFrame(fadeFrame)
  syncButtons(soundOn)

  if (soundOn) {
    play()
  } else if (audio) {
    // Silent anyway (e.g. play() still loading): stop right away.
    if (audio.volume === 0) {
      audio.pause()
    } else {
      fadeTo(0, () => audio.pause())
    }
  }
}

/** An explicit play / mute: remembered for the next visit. */
function choose(soundOn) {
  disarmResume()
  storage.set(STORAGE_KEY, soundOn ? 'on' : 'off')
  setState({ soundOn })
}

function resume(event) {
  if (event.type === 'pointerup' && event.pointerType === 'mouse') return
  if (event.type === 'keydown' && (INERT_KEYS.has(event.key) || event.ctrlKey || event.metaKey)) {
    return
  }
  // The footer buttons decide for themselves.
  if (event.target instanceof Element && event.target.closest(SOUND_BUTTONS)) return
  disarmResume()
  // Synchronously, inside the gesture — play() and the game's AudioContext
  // need it.
  setState({ soundOn: true })
}

function armResume() {
  if (resumeArmed) return
  resumeArmed = true
  // Capture phase, so a handler stopping propagation can't swallow it.
  RESUME_EVENTS.forEach((type) => document.addEventListener(type, resume, true))
}

function disarmResume() {
  resumeArmed = false
  RESUME_EVENTS.forEach((type) => document.removeEventListener(type, resume, true))
}

export function startSound() {
  syncButtons(false)

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-sound-play]')) {
      choose(true)
      emit('play')
    } else if (event.target.closest('[data-sound-mute]')) {
      choose(false)
    }
  })

  subscribe((state, changed) => {
    if (changed.includes('soundOn')) apply(state.soundOn)
  })

  // Don't keep playing into a background tab (a fade-out would stall there,
  // browsers don't run animation frames in hidden tabs).
  document.addEventListener('visibilitychange', () => {
    if (!audio) return
    if (document.hidden) {
      audio.pause()
    } else if (getState().soundOn) {
      play()
    }
  })

  if (storage.get(STORAGE_KEY) === 'on') armResume()
}
